import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUp,
  Gauge,
  Heart,
  Mountain,
  Sunrise,
  Sunset,
  Thermometer,
  TriangleAlert,
  UserRound,
  UsersRound,
  Waves,
  Wind,
} from "lucide-react";
import { getTidesByLocation } from "../../../../api"; // 4 levels up to src/api
import { trackProductEvent } from "../../../../analytics/trackEvent";
import {
  getWindArrow,
  normalizeWeatherConditionKey,
} from "../../../../utils/weatherPresentation"; // 4 levels up to src/utils
import MoonPhaseIcon from "../../../weather/MoonPhaseIcon";

function getClimateRiskClass(risk) {
  if (!risk) return "";
  return `climate-risk-${risk}`;
}

const HERO_WEATHER_ICON_PATHS = {
  clear: "/weather-icons/clear.png",
  "partly-cloudy": "/weather-icons/partly-cloudy.png",
  cloudy: "/weather-icons/cloudy.png",
  fog: "/weather-icons/fog.png",
  drizzle: "/weather-icons/drizzle.png",
  rain: "/weather-icons/rain.png",
  showers: "/weather-icons/showers.png",
  storm: "/weather-icons/storm.png",
  snow: "/weather-icons/snow.png",
  unknown: "/weather-icons/unknown.png",
};

function resolveHeroWeatherIconKey(conditionKey, conditionText) {
  const normalizedKey =
    typeof conditionKey === "string" ? conditionKey.replace(/_/g, "-") : "";
  if (HERO_WEATHER_ICON_PATHS[normalizedKey]) {
    return normalizedKey;
  }

  const text = typeof conditionText === "string" ? conditionText.toLowerCase() : "";
  if (text.includes("shower")) return "showers";
  if (text.includes("drizzle")) return "drizzle";
  if (text.includes("rain")) return "rain";
  if (text.includes("thunder") || text.includes("storm")) return "storm";
  if (text.includes("snow") || text.includes("sleet") || text.includes("blizzard")) return "snow";
  if (text.includes("fog") || text.includes("mist") || text.includes("haze")) return "fog";
  if (text.includes("partly")) return "partly-cloudy";
  if (text.includes("cloud") || text.includes("overcast")) return "cloudy";
  if (text.includes("clear") || text.includes("sunny")) return "clear";

  return "unknown";
}

function WeatherConditionIcon({
  conditionKey,
  conditionText,
  className = "weather-metric-icon",
}) {
  const iconKey = resolveHeroWeatherIconKey(conditionKey, conditionText);

  return (
    <img
      src={HERO_WEATHER_ICON_PATHS[iconKey]}
      alt=""
      aria-hidden="true"
      className={`${className} weather-hero-condition-image`}
    />
  );
}

function DepthMetricIcon({ depth }) {
  if (typeof depth !== "number") {
    return <Mountain aria-hidden="true" className="weather-metric-icon" />;
  }

  const Icon = depth < 0 ? ArrowDown : ArrowUp;

  return (
    <span className="weather-depth-icon" aria-hidden="true">
      <Mountain className="weather-metric-icon" />
      <Icon className="weather-depth-direction-icon" />
    </span>
  );
}

function TideTypeIcon({ type }) {
  const normalizedType = String(type || "").toLowerCase();
  const Icon = normalizedType.includes("high")
    ? ArrowUp
    : normalizedType.includes("low")
      ? ArrowDown
      : Waves;

  return <Icon aria-hidden="true" className="tide-type-icon" />;
}

function roundFavoriteCoordinate(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Number(number.toFixed(6));
}

function isSameFavoriteSpot(left, right) {
  const leftLat = roundFavoriteCoordinate(left?.lat);
  const leftLng = roundFavoriteCoordinate(left?.lng);
  const rightLat = roundFavoriteCoordinate(right?.lat);
  const rightLng = roundFavoriteCoordinate(right?.lng);

  return (
    leftLat !== null &&
    leftLng !== null &&
    leftLat === rightLat &&
    leftLng === rightLng
  );
}

export default function WeatherTab({
  data,
  onOpenAnglers,
  onOpenGroups,
  viewer,
  onAddFavoriteSpot,
  isSimpleModeActive = false,
}) {
  const { t } = useTranslation();
  const [tideInfo, setTideInfo] = useState(null);
  const [locationActionNotice, setLocationActionNotice] = useState("");
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
  const tideLat = data?.lat;
  const tideLng = data?.lng;
  const environment = data?.environment;

  // Tide fetching logic moved here to isolate it to the Weather Tab
  useEffect(() => {
    let active = true;

    (async () => {
      if (environment === "marine") {
        try {
          const res = await getTidesByLocation(tideLat, tideLng);
          if (active) setTideInfo(res);
        } catch (e) {
          console.warn("Tides fetch failed", e);
          if (active) setTideInfo(null);
        }
      } else {
        if (active) setTideInfo(null);
      }
    })();

    return () => {
      active = false;
    };
  }, [tideLat, tideLng, environment]);

  if (!data) return null;

  const w = data.weather?.condition || t("common_empty", { defaultValue: "—" });
  const weatherKey = normalizeWeatherConditionKey(data.weather?.condition);
  const favoriteSpots = Array.isArray(viewer?.favoriteSpots)
    ? viewer.favoriteSpots
    : [];
  const favoriteSpotPayload = {
    name:
      data.placeName ||
      `${data.lat?.toFixed?.(3)}, ${data.lng?.toFixed?.(3)}`,
    location:
      data.placeName ||
      `${data.lat?.toFixed?.(3)}, ${data.lng?.toFixed?.(3)}`,
    lat: data.lat,
    lng: data.lng,
  };
  const isFavorited = favoriteSpots.some((spot) =>
    isSameFavoriteSpot(spot, favoriteSpotPayload)
  );
  const favoriteButtonLabel = isFavorited
    ? t("weather_favorites_added_label")
    : t("weather_add_to_favorites");
  const airTemperatureLabel =
    typeof data.weather?.airTemp === "number"
      ? `${data.weather.airTemp.toFixed(1)} °C`
      : "";
  const feelsLikeTemperature = [
    data.weather?.feelsLike,
    data.weather?.feelsLikeTemp,
    data.weather?.apparentTemp,
    data.weather?.apparentTemperature,
  ].find((value) => typeof value === "number");
  const feelsLikeLabel =
    typeof feelsLikeTemperature === "number"
      ? t("weather_feels_like", {
          defaultValue: "Feels like {{temperature}}",
          temperature: `${feelsLikeTemperature.toFixed(1)} °C`,
        })
      : "";
  const showDepthCard = data.environment === "marine";

  return (
    <div className="tab-content">
      {!isSimpleModeActive && (
      <div className="location-action-row">
        <button
          type="button"
          aria-label={favoriteButtonLabel}
          disabled={isUpdatingFavorite}
          className={`location-action-button favorites-button ${isFavorited ? "favorited" : ""}`}
          onClick={async () => {
            setLocationActionNotice("");
            if (!onAddFavoriteSpot || isUpdatingFavorite) {
              return;
            }

            setIsUpdatingFavorite(true);
            try {
              const didUpdate = await onAddFavoriteSpot(favoriteSpotPayload);
              if (didUpdate && !isFavorited) {
                trackProductEvent("map_favorite_spot_added", {
                  featureArea: "map",
                  metadata: {
                    source: "weather_tab",
                    environment: data.environment || "",
                    has_place_name: Boolean(data.placeName),
                  },
                });
              }
              if (!didUpdate) {
                setLocationActionNotice(t("weather_favorites_update_failed"));
              }
            } finally {
              setIsUpdatingFavorite(false);
            }
          }}
        >
          <span className="location-action-heart" aria-hidden="true">
            <Heart fill={isFavorited ? "currentColor" : "none"} />
          </span>
          <span>{favoriteButtonLabel}</span>
        </button>
        <button
          type="button"
          className="location-action-button location-action-button-secondary"
          onClick={() => {
            setLocationActionNotice("");
            trackProductEvent("nearby_anglers_opened", {
              featureArea: "map",
              metadata: { source: "weather_tab" },
            });
            if (onOpenAnglers) {
              onOpenAnglers();
            }
          }}
        >
          <UserRound aria-hidden="true" /> {t("weather_anglers")}
        </button>
        <button
          type="button"
          className="location-action-button location-action-button-secondary"
          onClick={() => {
            setLocationActionNotice("");
            trackProductEvent("nearby_groups_opened", {
              featureArea: "map",
              metadata: { source: "weather_tab" },
            });
            if (onOpenGroups) {
              onOpenGroups();
            }
          }}
        >
          <UsersRound aria-hidden="true" /> {t("groups")}
        </button>
      </div>
      )}

      {locationActionNotice && !isSimpleModeActive ? (
        <p className="location-action-note">{locationActionNotice}</p>
      ) : null}

      <div className="weather-conditions-section">
        <div className="weather-hero-card">
          <div className="weather-hero-icon" aria-hidden="true">
            <WeatherConditionIcon
              conditionKey={weatherKey}
              conditionText={data.weather?.condition}
              className="weather-hero-condition-icon"
            />
          </div>
          <div className="weather-hero-main">
            <strong className="weather-hero-temperature">
              {airTemperatureLabel || t("common_na")}
            </strong>
            <span className="weather-hero-condition">{w}</span>
            {feelsLikeLabel ? (
              <span className="weather-hero-feels-like">{feelsLikeLabel}</span>
            ) : null}
          </div>
        </div>

        {data.moon && (
          <div className="moon-hero-card">
            <div className="moon-hero-icon" aria-hidden="true">
              <MoonPhaseIcon phase={data.moon.phase} />
            </div>
            <div className="moon-hero-main">
              <span className="moon-hero-label">{t("weather_label_moon")}</span>
              <strong className="moon-hero-value">
                {data.moon.phase || t("common_empty", { defaultValue: "—" })}
              </strong>
              {typeof data.moon.illumination === "number" ? (
                <span className="moon-hero-meta">
                  ({Math.round(data.moon.illumination * 100)}%)
                </span>
              ) : null}
            </div>
          </div>
        )}

        <div className="weather-detail-grid">
          {data.waterTemp != null && (
            <div className="weather-detail-card">
              <span className="weather-detail-card-label">
                <Thermometer aria-hidden="true" className="weather-metric-icon" />{" "}
                {t("weather_label_water_temp")}
              </span>
              <strong>{data.waterTemp.toFixed(1)} °C</strong>
            </div>
          )}

          <div className="weather-detail-card">
            <span className="weather-detail-card-label">
              <Wind aria-hidden="true" className="weather-metric-icon" />{" "}
              {t("weather_label_wind")}
            </span>
            <strong>
              {typeof data.weather?.windSpeed === "number"
                ? `${data.weather.windSpeed.toFixed(1)} km/h`
                : t("common_na")}
            </strong>
            <span className="weather-detail-card-meta">
              ({data.weather?.windDir || t("common_empty", { defaultValue: "—" })})
              {getWindArrow(
                data.weather?.windDirDeg,
                typeof data.weather?.windDirDeg === "number"
                  ? t("weather_wind_direction_aria", {
                      defaultValue: "Wind direction {{degrees}} degrees",
                      degrees: data.weather.windDirDeg,
                    })
                  : undefined
              )}
            </span>
          </div>

          <div className="weather-detail-card">
            <span className="weather-detail-card-label">
              <Gauge aria-hidden="true" className="weather-metric-icon" />{" "}
              {t("weather_label_gusts")}
            </span>
            <strong>
              {typeof data.weather?.gust === "number"
                ? `${data.weather.gust.toFixed(1)} km/h`
                : t("common_na")}
            </strong>
          </div>

          <div className="weather-detail-card">
            <span className="weather-detail-card-label">
              <Sunrise aria-hidden="true" className="weather-metric-icon" />{" "}
              {t("weather_label_sunrise")}
            </span>
            <strong>
              {data.weather?.sunrise || t("common_empty", { defaultValue: "—" })}
            </strong>
          </div>

          <div className="weather-detail-card">
            <span className="weather-detail-card-label">
              <Sunset aria-hidden="true" className="weather-metric-icon" />{" "}
              {t("weather_label_sunset")}
            </span>
            <strong>
              {data.weather?.sunset || t("common_empty", { defaultValue: "—" })}
            </strong>
          </div>

          {showDepthCard ? (
            <div className="weather-detail-card">
              <span className="weather-detail-card-label">
                <DepthMetricIcon depth={data.depth} /> {t("weather_labels_depth")}
              </span>
              <strong>
                {typeof data.depth === "number" ? (
                  <>{Math.abs(data.depth).toFixed(1)} m</>
                ) : (
                  t("common_na")
                )}
              </strong>
              {typeof data.depth === "number" ? (
                <span className="weather-detail-card-meta">
                  {data.depth < 0
                    ? t("weather_depth_below_sea")
                    : t("weather_depth_above_sea")}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* ---------------- Tides ---------------- */}
      {data.environment === "marine" && (
        <div className="tide-section">
          <h4>
            <Waves aria-hidden="true" className="tide-section-icon" />{" "}
            {t("weather_tide_title")}
          </h4>

          {tideInfo?.gauge && (
            <>
              <div className="weather-item">
                <b>{t("weather_tide_station")}:</b> {tideInfo.gauge.station}
              </div>

              <div className="weather-item">
                <b>{t("weather_tide_current_level")}:</b>{" "}
                {tideInfo.gauge.level != null
                  ? `${tideInfo.gauge.level.toFixed(2)} m`
                  : t("common_na")}
              </div>

 <div className="weather-item">
                <b>{t("weather_tide_as_of")}:</b>{" "}
                {tideInfo.gauge.time
                  ? new Date(tideInfo.gauge.time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : t("common_empty", { defaultValue: "—" })}
              </div>             
            </>
          )}

          {tideInfo?.predictions?.length ? (
            <>
              <h5 className="tide-predictions-title">
                {t("weather_tide_predicted_today")}
              </h5>

              <ul className="tide-predictions-list">
                {tideInfo.predictions.slice(0, 6).map((pred, i) => (
                  <li key={i}>
                    <TideTypeIcon type={pred.type} />
                    {pred.type
                      ? pred.type.toUpperCase()
                      : t("weather_tide_generic", { defaultValue: "TIDE" })}{" "}
                    —{" "}
                    {new Date(pred.time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {typeof pred.height === "number"
                      ? ` — ${pred.height.toFixed(2)} m`
                      : ""}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>{t("weather_tide_no_data")}</p>
          )}
        </div>
      )}

      {/* ---------------- Climate (LAST) ---------------- */}
      {data.climate && (
        <div className="climate-section">
          <h4>{t("weather_climate_title")}</h4>

          {/* Climate zone */}
          <div className="climate-zone">
            {t(`weather_climate_zone_${data.climate.zone}`, {
              defaultValue: data.climate.zone,
            })}
          </div>

          {/* Rainy season */}
          {data.climate.rainy && (
            <div className="weather-item">
              <b>{t("weather_climate_rainy")}:</b>{" "}
              {t("weather_climate_months")}:{" "}
              {data.climate.rainy.months.join(", ")} —{" "}
              {t("weather_climate_risk")}{" "}
              <span className={getClimateRiskClass(data.climate.rainy.risk)}>
                {t(`weather_climate_risk_${data.climate.rainy.risk}`, {
                  defaultValue: data.climate.rainy.risk,
                })}
              </span>
            </div>
          )}

          {/* Dry season */}
          {data.climate.dry && (
            <div className="weather-item">
              <b>{t("weather_climate_dry")}:</b>{" "}
              {t("weather_climate_months")}:{" "}
              {data.climate.dry.months.join(", ")} —{" "}
              {t("weather_climate_risk")}{" "}
              <span className={getClimateRiskClass(data.climate.dry.risk)}>
                {t(`weather_climate_risk_${data.climate.dry.risk}`, {
                  defaultValue: data.climate.dry.risk,
                })}
              </span>
            </div>
          )}

          {/* Hurricane / Storm */}
          {data.climate.storm && (
            <div className="weather-item hurricane-warning">
              <TriangleAlert
                aria-hidden="true"
                className="weather-metric-icon"
              />{" "}
              <b>{t("weather_climate_hurricane")}:</b>{" "}
              {t("weather_climate_months")}:{" "}
              {data.climate.storm.months.join(", ")} —{" "}
              {t("weather_climate_risk")}{" "}
              <span className={getClimateRiskClass(data.climate.storm.risk)}>
                {t(`weather_climate_risk_${data.climate.storm.risk}`, {
                  defaultValue: data.climate.storm.risk,
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
